import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Trophy, Volume2, ArrowLeft, Mic, MicOff } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const LETTERS = [
  { letter: "أ", word: "أسد", emoji: "🦁" },
  { letter: "ب", word: "بطة", emoji: "🦆" },
  { letter: "ت", word: "تفاحة", emoji: "🍎" },
  { letter: "ث", word: "ثعلب", emoji: "🦊" },
  { letter: "ج", word: "جمل", emoji: "🐫" },
  { letter: "ح", word: "حصان", emoji: "🐴" },
  { letter: "خ", word: "خروف", emoji: "🐑" },
  { letter: "د", word: "دب", emoji: "🐻" },
  { letter: "ذ", word: "ذرة", emoji: "🌽" },
  { letter: "ر", word: "رمان", emoji: "🍎" },
  { letter: "ز", word: "زرافة", emoji: "🦒" },
  { letter: "س", word: "سمكة", emoji: "🐟" },
  { letter: "ش", word: "شمس", emoji: "☀️" },
  { letter: "ص", word: "صقر", emoji: "🦅" },
  { letter: "ض", word: "ضفدع", emoji: "🐸" },
  { letter: "ط", word: "طائر", emoji: "🐦" },
  { letter: "ظ", word: "ظرف", emoji: "✉️" },
  { letter: "ع", word: "عصفور", emoji: "🐤" },
  { letter: "غ", word: "غزال", emoji: "🦌" },
  { letter: "ف", word: "فراشة", emoji: "🦋" },
  { letter: "ق", word: "قمر", emoji: "🌙" },
  { letter: "ك", word: "كلب", emoji: "🐕" },
  { letter: "ل", word: "ليمون", emoji: "🍋" },
  { letter: "م", word: "موزة", emoji: "🍌" },
  { letter: "ن", word: "نحلة", emoji: "🐝" },
  { letter: "هـ", word: "هرة", emoji: "🐱" },
  { letter: "و", word: "وردة", emoji: "🌹" },
  { letter: "ي", word: "يد", emoji: "✋" },
];

const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

const generateOptions = (correctLetter: string) => {
  const others = LETTERS.filter((l) => l.letter !== correctLetter);
  const shuffled = shuffleArray(others).slice(0, 3);
  const options = [...shuffled.map((l) => l.letter), correctLetter];
  return shuffleArray(options);
};

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function ArabicLettersGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds, timeout } = getDifficultyConfig(difficulty);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [round, setRound] = useState(0);
  const [completed, setCompleted] = useState(false);

  const currentLetter = LETTERS[currentIndex];

  const { speak, isSpeaking, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    onVoiceResult: (transcript) => {
      if (selected || round >= totalRounds) return;
      const normalizeAr = (s: string) => s.replace(/[^\u0600-\u06FF]/g, "").trim();
      const spoken = normalizeAr(transcript);
      const match = options.find(opt => spoken.includes(opt) || opt.includes(spoken));
      if (match) handleSelect(match);
    },
  });

  useEffect(() => {
    setOptions(generateOptions(currentLetter.letter));
  }, [currentIndex]);

  const handleSelect = (letter: string) => {
    if (selected) return;
    setSelected(letter);
    const correct = letter === currentLetter.letter;
    setIsCorrect(correct);

    if (correct) {
      setScore((s) => s + (streak >= 2 ? 15 : 10));
      setStreak((s) => s + 1);
      if (streak >= 2) setShowCelebration(true);
      encourageCorrect();
    } else {
      setStreak(0);
      encourageWrong();
    }

    setTimeout(() => {
      setSelected(null);
      setIsCorrect(null);
      setShowCelebration(false);
      setRound((r) => r + 1);
      setCurrentIndex(Math.floor(Math.random() * LETTERS.length));
    }, timeout);
  };

  const reset = () => {
    setScore(0);
    setStreak(0);
    setRound(0);
    setSelected(null);
    setIsCorrect(null);
    setCurrentIndex(0);
    setCompleted(false);
  };

  useEffect(() => {
    if (round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 15;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
    }
  }, [round]);

  if (round >= totalRounds) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div
              animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="text-6xl"
            >
              🏆
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">أحسنت! 🎉</h2>
            <p className="text-muted-foreground">
              حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة
            </p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 15)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`}
                />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Score Bar */}
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">
          ⭐ {score} نقطة
        </Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">
          {round + 1} / {totalRounds}
        </Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <Badge className="bg-gradient-to-r from-orange-400 to-red-500 text-white border-0 text-sm px-3 py-1">
              🔥 سلسلة {streak}
            </Badge>
          </motion.div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full gradient-hero"
          animate={{ width: `${((round + 1) / totalRounds) * 100}%` }}
          transition={{ type: "spring" }}
        />
      </div>

      {/* Main Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex + "-" + round}
          initial={{ opacity: 0, rotateY: 90 }}
          animate={{ opacity: 1, rotateY: 0 }}
          exit={{ opacity: 0, rotateY: -90 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Card className="shadow-elevated overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-emerald-400 to-teal-500 p-8 text-center text-white relative">
                <div className="absolute top-3 left-3 flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full"
                    onClick={() => speak(`ما هو الحرف الأول من كلمة ${currentLetter.word}؟`)}
                  >
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={isListening ? "destructive" : "ghost"}
                    size="icon"
                    className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : "text-white/80 hover:text-white hover:bg-white/20"}`}
                    onClick={() => isListening ? stopListening() : startListening()}
                    disabled={!!selected}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>
                <motion.p
                  className="text-7xl mb-2"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  {currentLetter.emoji}
                </motion.p>
                <p className="text-lg font-medium opacity-90">ما هو الحرف الأول من كلمة</p>
                <p className="text-3xl font-bold mt-1">{currentLetter.word} ؟</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* Options Grid */}
      <div className="grid grid-cols-2 gap-3">
        {options.map((opt, i) => {
          const isSelected = selected === opt;
          const showResult = selected !== null;
          const isAnswer = opt === currentLetter.letter;

          let bgClass = "bg-card hover:bg-accent border-2 border-border hover:border-primary/50";
          if (showResult && isAnswer) bgClass = "bg-emerald-100 border-2 border-emerald-500 dark:bg-emerald-900/30";
          else if (isSelected && !isAnswer) bgClass = "bg-red-100 border-2 border-red-400 dark:bg-red-900/30";

          return (
            <motion.button
              key={opt}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={!selected ? { scale: 1.05 } : {}}
              whileTap={!selected ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(opt)}
              disabled={!!selected}
              className={`rounded-xl p-5 text-center transition-all ${bgClass}`}
            >
              <span className="text-4xl font-bold text-foreground">{opt}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Celebration Overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            {[...Array(12)].map((_, i) => (
              <motion.span
                key={i}
                className="absolute text-3xl"
                initial={{ opacity: 1, x: 0, y: 0 }}
                animate={{
                  opacity: 0,
                  x: (Math.random() - 0.5) * 400,
                  y: (Math.random() - 0.5) * 400,
                  rotate: Math.random() * 360,
                }}
                transition={{ duration: 1.2 }}
              >
                {["⭐", "🌟", "✨", "🎉", "🎊"][i % 5]}
              </motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
